/* eslint-disable react-refresh/only-export-components */
import { useSearchParams } from "react-router-dom";
import { createContext, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import {
  DEFAULT_AVAILABLE_COLORS,
  DEFAULT_CACHED_STATE,
  VARIANTS_FILE_URL,
  ACCESSORIES_FILE_URL,
  CONFIGURATION_FILE_URL,
  FEATURES_AND_COLORS_FILE_URL,
  ACCESSORY_TYPES,
  MODELS,
  ALLOWED_QUERIES,
  DEFAULT_MODEL_KEY,
} from "shared/enum";
import { fetchDataFile } from "helpers/requests";
import {
  countAccessories,
  findVariant,
  findColor,
  normalizeModelCode,
  filterVariants,
} from "helpers/variant";

import type {
  CachedState,
  ColorOption,
  Variant,
  ConfigurationFile,
  FeaturesAndColorsFile,
  AccessoriesFile,
  SelectedAccessories,
  VariantOptions,
  DataProviderProps,
} from "types/app";

import type { Dispatch, SetStateAction } from "react";

const DEFAULT_SELECTED_ACCESSORIES = Object.values(ACCESSORY_TYPES).reduce((acc, accessoryType) => {
  return {
    ...acc,
    [accessoryType]: [],
  };
}, {} as SelectedAccessories);

type DataContextType = {
  loaded: boolean;
  loadError: boolean;
  variants: Variant[];
  accessories: AccessoriesFile;
  cachedState: CachedState;
  configuration: ConfigurationFile | Record<string, never>;
  availableColors: ColorOption[];
  variantFeatures: FeaturesAndColorsFile["features"];
  defaultCachedState: CachedState;
  animationBlacklist: FeaturesAndColorsFile["animationBlacklist"];
  selectedAccessories: SelectedAccessories;
  defaultSelectedAccessories: SelectedAccessories;
  setCachedState: Dispatch<SetStateAction<CachedState>>;
  setAvailableColors: Dispatch<SetStateAction<ColorOption[]>>;
  setSelectedAccessories: Dispatch<SetStateAction<SelectedAccessories>>;
};

export const DataContext = createContext<DataContextType>({
  loaded: false,
  loadError: false,
  variants: [],
  accessories: [],
  cachedState: DEFAULT_CACHED_STATE,
  configuration: {},
  availableColors: DEFAULT_AVAILABLE_COLORS,
  variantFeatures: {},
  defaultCachedState: DEFAULT_CACHED_STATE,
  animationBlacklist: {},
  selectedAccessories: DEFAULT_SELECTED_ACCESSORIES,
  defaultSelectedAccessories: DEFAULT_SELECTED_ACCESSORIES,

  /* eslint-disable */
  // @ts-ignore
  setCachedState: (state: CachedState | ((prev: CachedState) => CachedState)) => {},
  // @ts-ignore
  setAvailableColors: (state: ColorOption[] | ((prev: ColorOption[]) => ColorOption[])) => {},
  setSelectedAccessories: (
    // @ts-ignore
    state: SelectedAccessories | ((prev: SelectedAccessories) => SelectedAccessories),
  ) => {},
  /* eslint-enable */
});

export function DataProvider({ children }: DataProviderProps) {
  const [cachedState, setCachedState] = useState<CachedState>(DEFAULT_CACHED_STATE);
  const [selectedAccessories, setSelectedAccessories] = useState<SelectedAccessories>(
    DEFAULT_SELECTED_ACCESSORIES,
  );
  const [availableColors, setAvailableColors] = useState<ColorOption[]>(DEFAULT_AVAILABLE_COLORS);

  const [searchParams] = useSearchParams();

  const {
    data: configuration,
    isError: configError,
    isSuccess: configLoaded,
  } = useQuery({
    queryKey: ["configuration"],
    queryFn: () => fetchDataFile<ConfigurationFile>(CONFIGURATION_FILE_URL),
    onSuccess(data) {
      const updatedData = { ...data };
      const modelQuery = searchParams.get("model");

      updatedData.initState.model = MODELS[DEFAULT_MODEL_KEY];

      if (modelQuery) {
        const normalizedModelKey = normalizeModelCode(modelQuery);
        if (ALLOWED_QUERIES.includes(modelQuery)) {
          updatedData.initState.model = MODELS[normalizedModelKey] || updatedData.initState.model;
          updatedData.initState.variantId =
            MODELS[normalizedModelKey].variantId || updatedData.initState.variantId;

          /**
           * Select color for new model code if a model default is available
           */
          if (normalizedModelKey in updatedData.modelDefaults) {
            const { colorId } = updatedData.modelDefaults[normalizedModelKey];

            updatedData.initState.colorId = colorId;
          }
        }
      } else {
        /**
         * Select color for new model code if a model default is available
         */
        if (updatedData.initState.model.code in updatedData.modelDefaults) {
          const { colorId } = updatedData.modelDefaults[updatedData.initState.model.code];

          updatedData.initState.colorId = colorId;
        }
      }

      return updatedData;
    },
  });

  const {
    data: variants,
    isError: variantsError,
    isSuccess: variantsLoaded,
  } = useQuery({
    queryKey: ["variants"],
    queryFn: () => fetchDataFile<Variant[]>(VARIANTS_FILE_URL),
  });

  const {
    data: featureAndColors,
    isError: featureAndColorsError,
    isSuccess: featureAndColorsLoaded,
  } = useQuery({
    queryKey: ["featureAndColors"],
    queryFn: () => fetchDataFile<FeaturesAndColorsFile>(FEATURES_AND_COLORS_FILE_URL),
  });

  const {
    data: accessories,
    isError: accessoriesError,
    isSuccess: accessoriesLoaded,
  } = useQuery({
    queryKey: ["accessories"],
    enabled: false,
    queryFn: () => fetchDataFile<AccessoriesFile>(ACCESSORIES_FILE_URL),
  });

  const variant = findVariant(variants || [], cachedState.variantId);
  const selectedColor = findColor(configuration?.colors || [], cachedState.colorId);

  /**
   * Finds & loads the initial variant from config file
   */
  useEffect(() => {
    if (!configLoaded || !variantsLoaded || !featureAndColorsLoaded) return;

    let variantRequirements = {} as VariantOptions;
    if (configuration.initState.model.code in configuration.modelDefaults) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { colorId, ...rest } = configuration.modelDefaults[configuration.initState.model.code];
      variantRequirements = rest;
    }

    const [initVariant] = filterVariants(variants, {
      code: configuration.initState.model.code,
      ...variantRequirements,
    });

    if (initVariant) {
      return setCachedState({
        ...configuration.initState,
        variantId: initVariant.id,
      });
    } else {
      console.warn("Initial variant not found! Defaulting to first available variant...");
    }

    if (variants.length) {
      return setCachedState({
        ...configuration.initState,
        variantId: variants[0].id,
      });
    }

    console.error(
      "No variants found! Verify initial filters in configuration.json & availability in variants.json!",
    );
  }, [configLoaded, variantsLoaded, featureAndColorsLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Sets available colors after initial load & after every variant change
   */
  useEffect(() => {
    if (!configLoaded || !featureAndColorsLoaded || !variant) return;

    const { colorId } = cachedState;
    const variantCode = variant.code;

    let newColorList: ColorOption[];

    if (variantCode in featureAndColors.colors) {
      newColorList = configuration.colors.filter((color) =>
        featureAndColors.colors[variantCode].includes(color.id),
      );

      /**
       * To order colors as per the `features-and-colors.json`
       */
      // newColorList ==
      //   featureAndColors.colors[variantCode]
      //     .map((id) => findColor(configuration.colors, id))
      //     .filter((option) => !!option);
    } else {
      newColorList = configuration.colors;
    }

    if (newColorList.length === 0) {
      setAvailableColors([]);
      return;
    }

    const selectedColorExists = newColorList.some((c) => c.id === colorId);
    let newColorId = selectedColorExists ? colorId : newColorList[0].id;

    if (variantCode in configuration.modelDefaults) {
      const { colorId } = configuration.modelDefaults[variantCode];

      newColorId = colorId;
    }

    if (!selectedColorExists) {
      console.log(
        `Selected color (${selectedColor?.label}) not available for this variant (${variantCode}), updating to the first color in list.`,
      );

      setCachedState({
        ...cachedState,
        colorId: newColorList[0].id,
      });
    }

    setAvailableColors(newColorList);

    if (newColorId !== colorId) setCachedState((prev) => ({ ...prev, colorId: newColorId }));
  }, [variant?.code, configLoaded, featureAndColorsLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * For detecting & updating state after change in selected accessories
   */
  useEffect(() => {
    if (!cachedState.animations.doorsOpen) return;

    const hasAccessories = countAccessories(selectedAccessories) > 0;

    // Auto close doors, if open & any accessory is selected
    if (hasAccessories) {
      setCachedState({
        ...cachedState,
        animations: {
          ...cachedState.animations,
          doorsOpen: false,
        },
      });
    }
  }, [selectedAccessories]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <DataContext.Provider
      value={{
        loaded: configLoaded && variantsLoaded && featureAndColorsLoaded,
        loadError: configError || variantsError || accessoriesError || featureAndColorsError,
        variants: variantsLoaded ? variants : [],
        accessories: accessoriesLoaded ? accessories : [],
        configuration: configLoaded ? configuration : {},
        cachedState,
        availableColors,
        variantFeatures: featureAndColorsLoaded ? featureAndColors.features : {},
        defaultCachedState: DEFAULT_CACHED_STATE,
        animationBlacklist: featureAndColorsLoaded ? featureAndColors.animationBlacklist : {},
        selectedAccessories,
        defaultSelectedAccessories: DEFAULT_SELECTED_ACCESSORIES,
        setCachedState,
        setAvailableColors,
        setSelectedAccessories,
      }}
    >
      {children}
    </DataContext.Provider>
  );
}
